import useEmblaCarousel from "embla-carousel-react";
import { env } from "@/config/env";
import stars from "../assets/stars.webp";
import reviewerRachel from "../assets/reviewer.webp";
import reviewerPriya from "../assets/reviewer-priya.webp";
import reviewerDan from "../assets/reviewer-dan.webp";
import reviewerChelsea from "../assets/reviewer-chelsea.webp";
import reviewerOmar from "../assets/reviewer-omar.webp";
import satisfactionShield from "../assets/satisfaction-shield.webp";
import trustedHands from "../assets/trusted-hands.webp";
import laurelIcon from "../assets/icon-laurel.svg";
import ratingStars from "../assets/icon-rating-stars.svg";
import { useCountUp } from "../hooks/use-count-up";

const REVIEWS = [
  {
    name: "Rachel",
    avatar: reviewerRachel,
    text: "I didn't believe it at first, but they matched me to two settlements I never knew existed. Got my claim filed in five minutes.",
  },
  {
    name: "Priya",
    avatar: reviewerPriya,
    text: "Honestly didnt think it would work but it found a settlement from 2 yrs ago i totally forgot about. money came pretty quick too",
  },
  {
    name: "Dan",
    avatar: reviewerDan,
    text: "Was skeptical at first ngl. but they matched me with something in like a minute and filing was actually simple, not the usual headache",
  },
  {
    name: "Chelsea",
    avatar: reviewerChelsea,
    text: "I get so many spam emails about class actions and ignored all of them lol. this one was real tho, got my check",
  },
  {
    name: "Omar",
    avatar: reviewerOmar,
    text: "Easy process, they do all the digging for you. Found me a settlement I had zero idea about!!",
  },
] as const;

export function CheckoutTrust() {
  const { value, ref } = useCountUp(1_850_000);
  const [emblaRef] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
  });

  return (
    <section className="flex flex-col gap-10 px-4 pb-10 pt-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <img
            src={trustedHands}
            alt=""
            className="-mx-4 w-[calc(100%+32px)] max-w-none object-contain"
          />
          <p className="font-display text-xl font-bold leading-6 text-mf-neutral-400">
            Trusted by thousands
            <br />
            who&apos;ve already claimed over
          </p>
          <p
            ref={ref}
            className="font-display text-[48px] font-extrabold leading-[60px] text-mf-neutral-900"
          >
            ${value.toLocaleString("en-US")}
          </p>
        </div>

        <div className="-mx-4">
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-4 pl-4 pr-4">
              {REVIEWS.map((review) => (
                <div key={review.name} className="min-w-0 flex-[0_0_300px]">
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-sm leading-4">
          <span className="font-medium text-mf-neutral-900">*Disclaimer: </span>
          <span className="text-mf-neutral-400">
            This reflects the approximate total amount claimed by users through
            our service. Each user&apos;s case is unique, and results may vary
            based on eligibility and settlement outcomes.
          </span>
        </p>
      </div>

      <div className="h-px w-full bg-mf-neutral-200" />

      <div className="flex flex-col items-center gap-6">
        <div className="flex w-full flex-col items-center gap-4">
          <img
            src={satisfactionShield}
            alt=""
            className="h-[98px] w-20 object-contain"
          />
          <h3 className="text-center font-display text-xl font-bold leading-6 text-mf-neutral-900">
            Satisfaction Guaranteed
          </h3>
          <p className="whitespace-pre-wrap text-center text-sm leading-4 text-mf-neutral-600">
            {`We know finding money you're owed should feel simple, not stressful. That's why we've built a process that's fast, transparent, and actually works — no confusing forms, no hidden fees.\n\nBut if for any reason your experience doesn't feel effortless, we've got your back.`}
          </p>
        </div>
        <StatsCard />
      </div>
    </section>
  );
}

function StatsCard() {
  return (
    <div className="flex flex-col gap-3 rounded-[24px] bg-white px-4 pb-4 pt-3 shadow-[0_0_8px_rgba(26,22,19,0.03)]">
      <div className="flex items-center gap-2">
        <span className="relative h-20 w-[34px] shrink-0 overflow-hidden">
          <img src={laurelIcon} alt="" className="size-full" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5 text-center">
          <p className="w-full font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
            2,850,000+
          </p>
          <p className="w-full text-sm leading-4 text-mf-neutral-600">
            people already using {env.VITE_APP_NAME} to explore themselves
          </p>
        </div>
        <span className="relative h-20 w-[34px] shrink-0 overflow-hidden">
          <img src={laurelIcon} alt="" className="-scale-x-100 size-full" />
        </span>
      </div>
      <div className="flex items-center gap-4 rounded-lg bg-mf-green/10 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-base font-bold leading-5 text-mf-neutral-900">
            <span className="text-mf-green">4.9</span> out of 5
          </p>
          <p className="text-xs leading-4 text-mf-neutral-600">
            Based on verified responses
          </p>
        </div>
        <span className="relative h-5 w-[108px] shrink-0 overflow-hidden">
          <img src={ratingStars} alt="" className="size-full" />
        </span>
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: (typeof REVIEWS)[number] }) {
  return (
    <article className="flex h-[312px] w-full flex-col gap-4 rounded-[24px] bg-white px-4 pb-4 pt-5 shadow-[0_0_8px_rgba(26,22,19,0.03)]">
      <img src={stars} alt="" className="h-5 w-[124px]" />
      <p className="flex-1 text-lg font-bold leading-6 text-mf-neutral-900">
        {review.text}
      </p>
      <div className="flex items-center gap-2">
        <img
          src={review.avatar}
          alt=""
          className="size-10 rounded-full object-cover"
        />
        <span className="text-sm font-medium text-mf-neutral-900">
          {review.name}
        </span>
      </div>
    </article>
  );
}
